import { useMemo } from "react";
import { MimshakData } from "../interfaces/DTO/mimshakData";
import { InvestmentPathDetail } from "../interfaces/DTO/mislaka_xml_models";
import { fundService } from "../services/fundService";

const IRA_SUB_SPECIALIZATION = "בניהול אישי IRA";

export interface ExcludedPathInfo {
  fundName?: string;
  policyNumber?: string;
  amount: number;
  reason: "IRA" | "missing_data";
}

export interface ExposureResult {
  stockExposure: { average: number; amount: number };
  currencyExposure: { average: number; amount: number };
  foreignExposure: { average: number; amount: number };
  totalAmount: number;
  totalExcludedAmount: number;
  excludedPaths: ExcludedPathInfo[];
}

interface Options {
  isModified?: boolean;
}

export function useExposureCalculation(data: MimshakData[], options: Options = {}): ExposureResult {
  const { isModified = false } = options;

  return useMemo(() => {
    const allPaths: { path: InvestmentPathDetail; policyNumber: string }[] = [];
    const excludedPaths: ExcludedPathInfo[] = [];
    let total = 0;
    let totalExcluded = 0;

    for (const row of data) {
      const policies = row.data?.products?.flatMap(p => p.policies_or_accounts) || [];
      const allRowPaths: InvestmentPathDetail[] = policies.flatMap(p => p.investment_paths || []);
      const totalRowAccumulated = allRowPaths.reduce((sum, path) => sum + (path.accumulated_amount ?? 0), 0);

      if ((row.funds_sum ?? 0) > 0 && totalRowAccumulated === 0 && allRowPaths.length === 0) {
        totalExcluded += row.funds_sum ?? 0;
        excludedPaths.push({ policyNumber: row.policy_number, amount: row.funds_sum ?? 0, reason: "missing_data" });
        continue;
      }

      for (const policy of policies) {
        const policyPaths = policy.investment_paths ?? [];
        const scaledPaths = policyPaths.map(p => ({ path: p, scaleFactor: 1 }));

        const scaledTotal = scaledPaths.reduce((s, { path, scaleFactor }) => s + (path.accumulated_amount ?? 0) * scaleFactor, 0);

        const policyAccumulatedTotal = (policy.accumulated_savings ?? []).reduce((s, a) => s + a.total_savings, 0);
        const baseForScale = policyAccumulatedTotal > 0 ? policyAccumulatedTotal
          : row.funds_sum != null ? row.funds_sum
          : totalRowAccumulated;

        const toFundsScale = isModified && scaledTotal > 0 ? baseForScale / scaledTotal : 1;

        for (const { path, scaleFactor } of scaledPaths) {
          const adjustedPath = { ...path, accumulated_amount: (path.accumulated_amount ?? 0) * scaleFactor * toFundsScale };
          total += adjustedPath.accumulated_amount;
          allPaths.push({ path: adjustedPath, policyNumber: row.policy_number });
        }
      }
    }

    const fundMap = new Map<number, any>();
    for (const { path } of allPaths) {
      if (!fundMap.has(path.path_code)) {
        const fund = fundService.getFundFromCacheById(path.path_code);
        if (fund) fundMap.set(path.path_code, fund);
      }
    }

    let totalAccumulatedAmount = 0;
    let totalStockExposureAmount = 0;
    let totalCurrencyExposureAmount = 0;
    let totalForeignExposureAmount = 0;
    let weightedStockExposure = 0;
    let weightedCurrencyExposure = 0;
    let weightedForeignExposure = 0;

    for (const { path, policyNumber } of allPaths) {
      const fund = fundMap.get(path.path_code);
      const amount = path.accumulated_amount ?? 0;
      if (amount <= 0) continue;

      if (!fund) {
        totalExcluded += amount;
        excludedPaths.push({ fundName: path.path_name, policyNumber, amount, reason: "missing_data" });
        continue;
      }
      if (fund.sub_specialization === IRA_SUB_SPECIALIZATION) {
        totalExcluded += amount;
        excludedPaths.push({ fundName: fund.fund_name ?? path.path_name, policyNumber, amount, reason: "IRA" });
        continue;
      }

      if (fund.total_assets) {
        totalAccumulatedAmount += amount;
        if (fund.stock_market_exposure !== undefined) {
          const stock = fund.stock_market_exposure / fund.total_assets;
          totalStockExposureAmount += stock * amount;
          weightedStockExposure += stock * amount;
        }
        if (fund.foreign_currency_exposure !== undefined) {
          const currency = fund.foreign_currency_exposure / fund.total_assets;
          totalCurrencyExposureAmount += currency * amount;
          weightedCurrencyExposure += currency * amount;
        }
        if (fund.foreign_exposure !== undefined) {
          const foreign = fund.foreign_exposure / fund.total_assets;
          totalForeignExposureAmount += foreign * amount;
          weightedForeignExposure += foreign * amount;
        }
      } else {
        totalExcluded += amount;
        excludedPaths.push({ fundName: fund.fund_name ?? path.path_name, policyNumber, amount, reason: "missing_data" });
      }
    }

    return {
      stockExposure: {
        average: totalAccumulatedAmount ? weightedStockExposure / totalAccumulatedAmount : 0,
        amount: totalStockExposureAmount,
      },
      currencyExposure: {
        average: totalAccumulatedAmount ? weightedCurrencyExposure / totalAccumulatedAmount : 0,
        amount: totalCurrencyExposureAmount,
      },
      foreignExposure: {
        average: totalAccumulatedAmount ? weightedForeignExposure / totalAccumulatedAmount : 0,
        amount: totalForeignExposureAmount,
      },
      totalAmount: total,
      totalExcludedAmount: totalExcluded,
      excludedPaths,
    };
  }, [data, isModified]);
}
